// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGetRecommendedProducts } from "@ces/App/Services/xRecommendedProducts";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { delay } from "@ces/XDL/Utilities";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    LoadProducts,
} from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";

export const XngLoadProducts: typeof LoadProducts = async ({
    dispatch,
    getProductCollectionParameter,
    extraProductCollectionParameter,
    additionalSort,
    parameter,
    // XNG-Change
    getState,
}) => {
    const {
        carouselId,
        carouselType,
        numberOfProductsToDisplay,
        isProductDetailsPage,
        productId,
        isShellEdit,
        // XNG-Change
        onComplete,
        widgetId,
    } = parameter as any;
    if (!getProductCollectionParameter) {
        return;
    }

    // XNG-Change
    const state = getState();

    const extraProductOptions = extraProductCollectionParameter
        ? {
              getProductCollectionParameter: extraProductCollectionParameter,
              numberOfProductsToDisplay,
          }
        : undefined;

    const isRecentlyViewedOnPDP = carouselType === "recentlyViewed" && isProductDetailsPage;

    // XNG-Change: add properties to the expand
    getProductCollectionParameter.expand
        ? getProductCollectionParameter.expand.push("images", "badges", "properties")
        : (getProductCollectionParameter.expand = ["images", "badges", "properties"]);

    // <XNG-Change> refetch recently viewed
    const lastTimeRecentlyViewed = (state as CustomAppState).xCommon.lastTimeRecentlyViewed;
    const time = Date.now();
    if (carouselType === "recentlyViewed") {
        const isExceed = lastTimeRecentlyViewed ? time - lastTimeRecentlyViewed >= 30000 : true;
        if (isExceed) {
            dispatch({
                type: "xCommon/update",
                data: {
                    key: "lastTimeRecentlyViewed",
                    value: time,
                },
            } as any);
            // Clean up previous one if any
            if (lastTimeRecentlyViewed) {
                dispatch({
                    type: "Data/Products/CompleteLoadProducts",
                    collection: { products: [] } as any,
                    parameter: { ...getProductCollectionParameter, time: lastTimeRecentlyViewed },
                });
            }
            (getProductCollectionParameter as any).time = time;
            await delay();
        } else {
            (getProductCollectionParameter as any).time = lastTimeRecentlyViewed;
        }
    }

    // if xRecommended
    if (carouselType === "xRecommendedProducts") {
        getProductCollectionParameter.type = carouselType;
        getProductCollectionParameter.pageSize = 16;

        const productsDataViewState = getProductsDataView(state, getProductCollectionParameter);
        if (!productsDataViewState.value) {
            try {
                const collection = await xGetRecommendedProducts({
                    widgetId,
                });
                dispatch({
                    type: "Data/Products/CompleteLoadProducts",
                    collection,
                    parameter: getProductCollectionParameter,
                });
            } catch {
                dispatch({
                    type: "Data/Products/CompleteLoadProducts",
                    collection: {
                        products: [],
                        pagination: {
                            page: 1,
                        },
                    } as any,
                    parameter: getProductCollectionParameter,
                });
            }
            await delay(1000);
        }
    }
    // </XNG-Change>

    dispatch(
        loadProductInfoList({
            id: carouselId,
            getProductCollectionParameter,
            extraProductOptions,
            excludeProductId: isRecentlyViewedOnPDP ? productId : undefined,
            forceLoad: isRecentlyViewedOnPDP,
            isShellEdit,
            additionalSort,
            // Add on complete
            onComplete,
        }),
    );
};

replaceInChain(chain, LoadProducts, XngLoadProducts);
